import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCcw } from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CronJobItem, listCronJobs } from "@/services/cronJobsApi";

function fmtDuration(s: number | null): string {
  if (s == null) return "—";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m${s % 60}s`;
}

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

function statusVariant(s: string | null): "default" | "destructive" | "secondary" | "outline" {
  if (s === "Succeeded") return "default";
  if (s === "Failed") return "destructive";
  if (s === "Running") return "secondary";
  return "outline";
}

export default function CronJobsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CronJobItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCronJobs(navigate);
      setItems(data.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-lg font-semibold">Jobs cron</h1>
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
              <RefreshCcw className={`mr-1 h-3 w-3 ${loading ? "animate-spin" : ""}`} />
              Rafraîchir
            </Button>
          </div>
        </header>

        <main className="flex flex-col gap-4 p-6">
          <p className="text-sm text-muted-foreground">
            CronJobs Kubernetes du namespace <code>recrutauto</code> (prod) et leur
            dernier run.
          </p>

          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Planification</TableHead>
                  <TableHead>Actif</TableHead>
                  <TableHead>Dernier run</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>Dernière exécution</TableHead>
                  <TableHead>Dernier succès</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.name}>
                    <TableCell className="font-mono text-xs">
                      {c.name}
                      {c.suspend && (
                        <Badge variant="outline" className="ml-2">
                          suspendu
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.schedule}</TableCell>
                    <TableCell>{c.active > 0 ? `${c.active} ▶` : "—"}</TableCell>
                    <TableCell>
                      {c.last_run_status ? (
                        <Badge variant={statusVariant(c.last_run_status)}>
                          {c.last_run_status}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>{fmtDuration(c.last_run_duration_seconds)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(c.last_schedule_time)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(c.last_successful_time)}</TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && !loading && !error && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      Aucun CronJob.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
